import React, { useState } from 'react';
import styled from 'styled-components';
import { siteConfig } from '../../config/site';

export const FAQS: { q: string; a: string }[] = [
  { q: `What is ${siteConfig.brand.name}?`,
    a: `${siteConfig.brand.name} is a licensed flash USDT software that displays large USDT balances on supported wallets and explorers. It's designed for demos, testing, training, and network validation — not for transferring real value. Always follow local laws and verify claims independently.` },
  { q: 'Is flash USDT real USDT?',
    a: `No. Flash USDT is a simulated balance that may appear authentic in some interfaces but does not represent real transferred funds.` },
  { q: 'Which networks does the software support?',
    a: 'TRC-20 (Tron), ERC-20 (Ethereum), and BEP-20 (BNB Smart Chain). Which chains your license unlocks depends on the plan — check the pricing page.' },
  { q: 'How long does a flash USDT balance stay active?',
    a: 'Flashes stay live for up to 365 days depending on the plan and network. Confirm exact details with support before purchasing.' },
  { q: 'What is the maximum amount I can flash daily?',
    a: 'Daily caps depend on plan tier: Demo License is one-time up to $40, Basic License $500,000/day for a year, Pro License (Lifetime) up to $10,000,000/day.' },
  { q: 'How is the flash USDT software delivered?',
    a: 'Within minutes of payment confirmation. You receive the license key and download link by email; WhatsApp support is available 24/7 for onboarding.' },
  { q: 'Which wallets are compatible with the flash USDT?',
    a: 'Compatible with 50+ wallets including Trust Wallet, MetaMask, Binance Wallet, Coinbase Wallet, SafePal, TronLink, and Exodus. Use the latest official app build on your chosen network.' },
  { q: 'How do I choose between TRC-20, ERC-20 and BEP-20?',
    a: 'TRC-20 is often preferred for low fees and fast confirmations on Tron-native wallets. ERC-20 fits MetaMask/DeFi-heavy workflows. BEP-20 balances low fees with broad Binance-ecosystem adoption. Match the network your audience already uses.' },
];

const Section = styled.section`
  padding: 5rem 1rem;
  background: var(--background-dark);
`;

const Inner = styled.div` max-width: 860px; margin: 0 auto; `;

const Head = styled.div`
  text-align: center;
  margin-bottom: 2.5rem;
  .tag {
    display: inline-block;
    padding: 0.35rem 0.9rem;
    border-radius: 999px;
    border: 1px solid rgba(255,255,255,0.15);
    background: rgba(0, 153, 0, 0.08);
    color: var(--primary-color);
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 1rem;
  }
  h2 { font-size: clamp(1.75rem, 3.2vw, 2.5rem); margin: 0 0 0.5rem; color: var(--text-color); span { color: var(--primary-color); } }
  p { color: var(--text-secondary); line-height: 1.7; margin: 0; }
`;

const Item = styled.div<{ $open: boolean }>`
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--border-radius);
  margin-bottom: 0.75rem;
  background: rgba(255, 255, 255, 0.02);
  overflow: hidden;
  transition: border-color 0.2s ease;
  &:hover { border-color: rgba(0, 153, 0, 0.4); }
  ${({ $open }) => $open && 'border-color: rgba(0, 153, 0, 0.6);'}
`;

const Question = styled.button<{ $open: boolean }>`
  width: 100%;
  padding: 1.15rem 1.25rem;
  background: transparent;
  border: 0;
  color: var(--text-color);
  font-size: 1rem;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  ${({ $open }) => $open && 'color: var(--primary-color);'}
  &:hover { color: var(--primary-color); }
  .icon { font-size: 1.4rem; line-height: 1; transition: transform 0.25s ease; transform: rotate(${({ $open }) => ($open ? '45deg' : '0')}); }
`;

const Answer = styled.div<{ $open: boolean }>`
  padding: ${({ $open }) => ($open ? '0 1.25rem 1.25rem' : '0 1.25rem')};
  max-height: ${({ $open }) => ($open ? '600px' : '0')};
  overflow: hidden;
  transition: all 0.3s ease;
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: 0.95rem;
`;

const CTA = styled.div`
  text-align: center;
  margin-top: 2.5rem;
  p { color: var(--text-secondary); margin-bottom: 1rem; }
  a {
    display: inline-block;
    background: var(--primary-color);
    color: var(--text-color);
    padding: 0.75rem 1.75rem;
    border-radius: var(--border-radius);
    font-weight: 700;
    text-decoration: none;
    transition: transform 0.2s ease;
    &:hover { transform: translateY(-2px); }
  }
`;

const FAQNew: React.FC = () => {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <Section id="faq">
      <Inner>
        <Head>
          <div className="tag">FAQ</div>
          <h2>Flash USDT — <span>common questions</span></h2>
          <p>Everything you need to know before you buy {siteConfig.brand.name}.</p>
        </Head>
        {FAQS.map((item, i) => (
          <Item key={i} $open={open === i}>
            <Question $open={open === i} onClick={() => setOpen(open === i ? null : i)}>
              <span>{item.q}</span>
              <span className="icon">+</span>
            </Question>
            <Answer $open={open === i}>{item.a}</Answer>
          </Item>
        ))}
        <CTA>
          <p>Still have a question?</p>
          <a href={`https://wa.me/${siteConfig.contact.whatsapp}`} target="_blank" rel="noopener noreferrer">Message us on WhatsApp</a>
        </CTA>
      </Inner>
    </Section>
  );
};

export default FAQNew;
